import React, { useState } from 'react';
import { Users, Plus, Search, Calendar, Check, Edit2 } from 'lucide-react';
import { useConfig } from '../hooks/useConfig';
import { getCleanEstilo, setStoredFeatureOverride } from '../utils/businessConfigHelpers';
import { supabase } from '../supabaseClient';
import WhatsAppProvisioningV2 from '../components/WhatsAppProvisioningV2';
import BotTestModeCard from '../components/BotTestModeCard';
import LidLinkSuggestions from '../components/LidLinkSuggestions';
import OrderSiteSettings from '../components/OrderSiteSettings';
const TooltipIcon = ({ text }) => (
  <span className="relative group inline-block cursor-help ml-1">
    <span className="w-4 h-4 rounded-full bg-slate-700 text-slate-300 text-[10px] font-bold flex items-center justify-center border border-slate-600">?</span>
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-800 text-xs text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-xl border border-slate-600">
      {text}
      <svg className="absolute top-full left-1/2 -translate-x-1/2 w-3 h-3 text-slate-800" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21l-12-18h24z"/></svg>
    </span>
  </span>
);

export default function ConfigPage() {
  const {
    activeBusiness,
    activeBusinessId,
    updateBusinessFeature,
    updateActiveBusinessField,
    saveBusinessSettings,
    showNotification,
    isSuperAdmin,
    isTrialAccount,
    isDemoAccount
  } = useConfig();

  const [manualMutePhone, setManualMutePhone] = useState("");
  const [manualMuteName, setManualMuteName] = useState("");
  const [mutedPhoneSearch, setMutedPhoneSearch] = useState("");
  const [selectedCountryPrefix, setSelectedCountryPrefix] = useState("57");
  const [lidPromptPhone, setLidPromptPhone] = useState(null);
  
  if (!activeBusiness) return <div>Cargando...</div>;

  const showBotEnabled = activeBusiness.bot_activo !== false;
  const isImpersonating = true; // In a full port this comes from auth context

  const handleToggleMutePhone = (phone, name = "") => {
    let cleanPhone = String(phone).replace(/\D/g, '');
    if (!cleanPhone) return;

    // Número local (sin indicativo): se le antepone el país elegido.
    if (selectedCountryPrefix && cleanPhone.length <= 10 && !cleanPhone.startsWith(selectedCountryPrefix)) {
      cleanPhone = selectedCountryPrefix + cleanPhone;
    }

    const currentMuted = Array.isArray(activeBusiness.muted_phones) ? activeBusiness.muted_phones : [];
    const suffix = (p) => { const d = String(p).replace(/\D/g, ''); return d.length >= 10 ? d.slice(-10) : d; };
    const isMuted = currentMuted.some(p => suffix(p) === suffix(cleanPhone));

    const newMetadata = { ...(activeBusiness.muted_phones_metadata || {}) };
    let newMuted;
    if (isMuted) {
      newMuted = currentMuted.filter(p => {
        if (suffix(p) !== suffix(cleanPhone)) return true;
        delete newMetadata[p];
        return false;
      });
    } else {
      newMuted = [...currentMuted, cleanPhone];
      if (name && name.trim()) newMetadata[cleanPhone] = name.trim();
    }

    // Un solo guardado: actualiza la pantalla y la base de datos con la lista completa y los nombres.
    saveBusinessSettings({
      muted_phones: newMuted,
      muted_phones_metadata: newMetadata
    });

    showNotification(isMuted ? "Chat reactivado para el bot" : "Chat silenciado", isMuted ? "success" : "info");
    setLidPromptPhone(isMuted ? null : cleanPhone);
  };

  const handleConnectCalendar = async () => {
    try {
      // Guardamos el ID del negocio activo para actualizar su token al regresar
      localStorage.setItem('keylacrm_connecting_calendar_id', activeBusinessId);
      showNotification("Redirigiendo a Google...", "info");

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.hostname === 'localhost'
            ? `${window.location.origin}/v2/config`
            : 'https://keyla.seryiads.com/v2/config',
          scopes: 'https://www.googleapis.com/auth/calendar.events',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error("Error conectando calendario:", err);
      showNotification("Error al conectar Google Calendar.", "error");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading">Configuración General de la IA</h2>
        <p className="text-xs text-seryi-text">Ajusta el comportamiento base de Keyla, horarios, y la información central de tu negocio.</p>
      </div>

      <div className="bg-gradient-to-r from-seryi-bg to-seryi-bg-dark border border-seryi-bg-light p-4 rounded-2xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-seryi-green/10 flex items-center justify-center text-seryi-green shrink-0 relative">
            <div className={`absolute inset-0 border-2 rounded-xl border-dashed ${showBotEnabled ? 'border-emerald-500 animate-[spin_4s_linear_infinite]' : 'border-rose-500'}`}></div>
            {showBotEnabled ? <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_#10b981]"></div> : <div className="w-3 h-3 bg-rose-500 rounded-full"></div>}
          </div>
          <div>
            <h4 className="font-bold text-sm text-seryi-heading flex items-center gap-2">
              ESTADO DEL BOT
              <TooltipIcon text="Pausa el bot globalmente para TODOS los chats (ej. fuera de horario)." />
            </h4>
            <p className="text-[11px] text-seryi-heading mt-0.5">
              {showBotEnabled ? "El asistente está respondiendo consultas." : "El asistente está en pausa para TODOS los chats."}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => updateBusinessFeature(activeBusinessId, 'bot_activo', !showBotEnabled)}
          className={`px-4 py-2.5 rounded-xl font-bold text-xs shrink-0 transition-all shadow-md active:scale-95 ${showBotEnabled ? 'bg-rose-600 hover:bg-rose-700 text-seryi-heading' : 'bg-emerald-600 hover:bg-emerald-700 text-seryi-heading'}`}
        >
          {showBotEnabled ? "Pausar Bot" : "Encender Bot"}
        </button>
      </div>

      <BotTestModeCard />

      {/* GESTOR DE CHATS PAUSADOS */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4">
        <div>
          <h3 className="font-extrabold text-xs text-seryi-heading flex items-center gap-2 uppercase tracking-wider">
            <Users className="text-seryi-green" size={16} /> Atención Humana Directa <TooltipIcon text="Chats silenciados. Un asesor puede silenciar al bot en el chat de un cliente escribiendo #pausar desde el WhatsApp del negocio, y devolverlo con #activar. El bot reacciona con un emoji y no le escribe nada al cliente." />
          </h3>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <input
              type="text"
              placeholder="Nombre (opcional)"
              value={manualMuteName}
              onChange={(e) => setManualMuteName(e.target.value)}
              className="w-full sm:w-1/3 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none placeholder:text-seryi-text"
            />
            <div className="flex w-full sm:w-2/3 items-center gap-2">
              <select
                value={selectedCountryPrefix}
                onChange={(e) => setSelectedCountryPrefix(e.target.value)}
                className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none shrink-0"
              >
                <option value="57">🇨🇴 +57</option>
                <option value="52">🇲🇽 +52</option>
                <option value="1">🇺🇸 +1</option>
                <option value="54">🇦🇷 +54</option>
                <option value="">🌍 Otro</option>
              </select>
              <input
                type="text"
                placeholder="Número local"
                value={manualMutePhone}
                onChange={(e) => setManualMutePhone(e.target.value)}
                className="flex-1 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none placeholder:text-seryi-text"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => { handleToggleMutePhone(manualMutePhone, manualMuteName); setManualMutePhone(''); setManualMuteName(''); }}
            className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 px-4 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 w-full"
          >
            <Plus size={14} /> Silenciar
          </button>
        </div>

        {lidPromptPhone && (
          <LidLinkSuggestions key={lidPromptPhone} businessId={activeBusinessId} phone={lidPromptPhone} onClose={() => setLidPromptPhone(null)} />
        )}

        <div className="space-y-2 pt-1">
          {activeBusiness.muted_phones?.length > 0 && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" size={14} />
              <input
                type="text"
                placeholder="🔍 Buscar por nombre o número de teléfono..."
                value={mutedPhoneSearch}
                onChange={(e) => setMutedPhoneSearch(e.target.value)}
                className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl pl-9 pr-4 py-2.5 text-xs text-seryi-heading focus:outline-none placeholder:text-seryi-text transition-colors focus:border-seryi-green/50"
              />
            </div>
          )}
          {(!activeBusiness.muted_phones || activeBusiness.muted_phones.length === 0) ? (
            <p className="text-xs text-seryi-text py-2">Aún no has silenciado ningún número. Cuando agregues uno, aparecerá aquí y el bot dejará de responderle.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(() => {
                const filteredMutedPhones = activeBusiness.muted_phones.filter(phone => {
                  if (!mutedPhoneSearch) return true;
                  const searchLower = mutedPhoneSearch.toLowerCase();
                  const name = activeBusiness.muted_phones_metadata?.[phone] || "Desconocido";
                  return phone.toLowerCase().includes(searchLower) || name.toLowerCase().includes(searchLower);
                });
                
                if (filteredMutedPhones.length === 0) {
                  return <div className="col-span-full text-center py-4 text-xs text-seryi-text">No se encontraron contactos silenciados.</div>;
                }
                
                return filteredMutedPhones.map(phone => {
                  const name = activeBusiness.muted_phones_metadata?.[phone] || "Desconocido";
                  return (
                    <div key={phone} className="bg-seryi-bg-dark border border-seryi-bg-light/80 p-3 rounded-2xl flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 overflow-hidden">
                        <span className="w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_#f43f5e] shrink-0"></span>
                        <div className="flex flex-col truncate group">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-seryi-heading text-[11px] truncate">{name}</span>
                          </div>
                          <span className="font-mono text-[11px] text-seryi-heading">+{phone}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleMutePhone(phone)}
                        className="text-[10px] bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-bold px-2.5 py-1 rounded-lg transition-all ml-2"
                      >
                        Reactivar
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      {/* CONEXIÓN WHATSAPP AUTO-QR (V2) */}
      <WhatsAppProvisioningV2
        activeBusiness={activeBusiness}
        isSuperAdmin={isSuperAdmin}
        isTrialAccount={isTrialAccount}
        isDemoAccount={isDemoAccount}
      />

      {/* DETALLES DEL NEGOCIO */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Nombre del Local</label>
            <input 
              type="text" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none"
              value={activeBusiness.nombre_negocio || ''}
              onChange={(e) => updateActiveBusinessField('nombre_negocio', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Categoría</label>
            <select 
              disabled={!isImpersonating}
              className={`w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none ${!isImpersonating ? 'opacity-60 cursor-not-allowed bg-seryi-bg-dark/40' : ''}`}
              value={activeBusiness.tipo_negocio || ''}
              onChange={(e) => updateActiveBusinessField('tipo_negocio', e.target.value)}
            >
              <option value="Barbería">💈 Barbería</option>
              <option value="Estética">💅 Estética</option>
              <option value="Clínica">🏥 Clínica</option>
              <option value="Restaurante">🍔 Restaurante</option>
              <option value="General">🏢 General</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">URL / Slug Público</label>
            <input 
              type="text" 
              placeholder="ej: mibusiness"
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none"
              value={activeBusiness.slug || ''}
              onChange={(e) => updateActiveBusinessField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">ID WhatsApp</label>
            <input 
              type="text" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none"
              value={activeBusiness.id_whatsapp || ''}
              onChange={(e) => updateActiveBusinessField('id_whatsapp', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Correo / Calendario</label>
            <input 
              type="text" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none"
              value={activeBusiness.google_calendar_id || ''}
              onChange={(e) => updateActiveBusinessField('google_calendar_id', e.target.value)}
            />
            {activeBusiness.feature_appointments && (
              <div className="pt-2">
                {activeBusiness.google_refresh_token ? (
                  <div className="w-full flex items-center justify-center gap-2 bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 px-3 py-2 rounded-xl text-xs font-bold">
                    <Check size={14} /> Calendario Vinculado
                  </div>
                ) : (
                  <button
                    onClick={handleConnectCalendar}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 px-3 py-2 rounded-xl text-xs font-bold hover:bg-blue-600/30 transition-all"
                  >
                    <Calendar size={14} /> Conectar Google Calendar
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {isSuperAdmin && (
          <div className="space-y-1">
            <label htmlFor="modo-negocio" className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Modo de atención del bot</label>
            <select
              id="modo-negocio"
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={['ventas', 'pedidos'].includes(activeBusiness.flujo_operativo) ? activeBusiness.flujo_operativo : 'estandar'}
              onChange={(e) => updateActiveBusinessField('flujo_operativo', ['ventas', 'pedidos'].includes(e.target.value) ? e.target.value : 'citas')}
            >
              <option value="estandar">Estándar: citas o pedidos, según el tipo de negocio</option>
              <option value="ventas">Ventas: quien escribe primero entra como prospecto</option>
              <option value="pedidos">Pedidos y domicilios: el bot toma los pedidos (agua, tienda, distribuidora)</option>
            </select>
            <p className="text-[10px] text-seryi-text/80">
              En modo ventas, un número que no está en Prospección se agrega solo como “Contactado” y el bot le contesta como vendedora (precio primero, una pregunta, estados en el CRM).
              En modo pedidos, el bot arma el pedido con el cliente (productos, cantidades, dirección y forma de pago) y lo guarda en Pedidos; el negocio necesita el módulo Pedidos encendido y sus productos con precio.
              Solo lo ve el administrador. Pulsa “Guardar Configuración Base” para aplicarlo.
            </p>
          </div>
        )}

        {activeBusiness.flujo_operativo === 'pedidos' && (
          <OrderSiteSettings key={activeBusinessId} business={activeBusiness} onChange={updateActiveBusinessField} />
        )}

        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Identidad Base (Eres Keyla...)</label>
          <textarea 
            rows={6}
            className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-4 text-xs text-seryi-heading focus:outline-none font-sans leading-relaxed"
            value={(activeBusiness.estilo_clean && activeBusiness.estilo_clean.trim()) ? activeBusiness.estilo_clean : getCleanEstilo(activeBusiness.estilo)}
            onChange={(e) => {
              const val = e.target.value;
              updateActiveBusinessField('estilo_clean', val);
              updateActiveBusinessField('estilo', val);
              setStoredFeatureOverride(activeBusinessId, 'estilo_clean', val);
            }}
          />
        </div>

        <div className="space-y-1 mt-4">
          <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">Instrucciones Adicionales del Negocio (Promociones, Catálogo)</label>
          <textarea 
            rows={5}
            className="w-full bg-slate-800/80 border border-emerald-500/30 rounded-xl p-4 text-xs text-slate-300 focus:outline-none focus:border-emerald-500/60 font-sans leading-relaxed"
            placeholder="Ej: Si alguien pregunta por alisado, diles que hoy tenemos 50% off..."
            value={activeBusiness.custom_instructions || ''}
            onChange={(e) => {
              const val = e.target.value;
              updateActiveBusinessField('custom_instructions', val);
              setStoredFeatureOverride(activeBusinessId, 'custom_instructions', val);
            }}
          />
        </div>

        <button 
          onClick={() => saveBusinessSettings()}
          className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-3 rounded-xl text-sm transition-all"
        >
          Guardar Configuración Base
        </button>
      </div>
    </div>
  );
}
